import { nowWithOffsetDate, timeNow } from './clock';

export const CALLSIGN_SEPARATORS: readonly string[] = [
  '->',
  '>',
  '-',
  ':',
  '_',
  ';',
].sort((a, b) => b.length - a.length);

export function parseCallSign(
  callSign: string,
  myCallsign: string,
): {
  from: string;
  to: string;
} | null {
  callSign = callSign.trim().toUpperCase();

  // Empty string -> both from and to are empty
  if (callSign === '')
    return {
      from: '',
      to: '',
    };

  // Escape for regex
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(CALLSIGN_SEPARATORS.map(esc).join('|'), 'g');

  const matches = callSign.match(regex) || [];

  // If the string is exactly a separator or contains multiple separators, treat as invalid
  if (matches.length === 0) {
    // no separator found -> from is the callSign, to is myCallsign
    return { from: callSign, to: myCallsign };
  }
  if (matches.length > 1) return null;

  const sep = matches[0]!;
  if (sep === callSign)
    return {
      from: '',
      to: '',
    };

  const idx = callSign.indexOf(sep);
  if (idx === 0) {
    // leading separator: ->TO or >TO or -TO
    return { from: myCallsign, to: callSign.slice(sep.length).trim() };
  }
  if (idx + sep.length === callSign.length) {
    // trailing separator: FROM-> or FROM>
    return { from: callSign.slice(0, -sep.length).trim(), to: myCallsign };
  }

  // middle separator: FROM->TO
  const from = callSign.slice(0, idx).trim();
  const to = callSign.slice(idx + sep.length).trim();
  return { from, to };
}

export function parseTime(timeStr: string, draft = false): string | null {
  timeStr = timeStr.trim();

  // Empty string -> current time
  if (timeStr === '') {
    return draft ? '' : timeNow();
  }

  // Relative time: +/- minutes or hours:minutes
  if (timeStr.startsWith('+') || timeStr.startsWith('-')) {
    const isPositive = timeStr.startsWith('+');
    const timeValue = timeStr.slice(1);

    let minutes = 0;
    if (timeValue.includes(':')) {
      const [hours, mins] = timeValue.split(':');
      minutes = (parseInt(hours!) || 0) * 60 + (parseInt(mins!) || 0);
    } else {
      const num = parseInt(timeValue);
      if (num >= 100) {
        // e.g., 110 = 1 hour 10 minutes
        minutes = Math.floor(num / 100) * 60 + (num % 100);
      } else {
        minutes = num;
      }
    }

    const now = nowWithOffsetDate();
    now.setMinutes(now.getMinutes() + (isPositive ? minutes : -minutes));
    return now.toISOString();
  }

  // AM/PM format (e.g., "830P", "8:30PM", "8 30 P", "8h30P")
  const amPmMatch = timeStr.match(
    /^(\d{1,2})[\s:hH]?(\d{2})?\s*([APap])[Mm]?$/,
  );
  if (amPmMatch) {
    let hours = parseInt(amPmMatch[1]!);
    const minutes = parseInt(amPmMatch[2] || '0');
    const period = amPmMatch[3]!.toUpperCase();

    if (period === 'P' && hours !== 12) {
      hours += 12;
    } else if (period === 'A' && hours === 12) {
      hours = 0;
    }

    const now = nowWithOffsetDate();
    now.setHours(hours, minutes, 0, 0);
    return now.toISOString();
  }

  // 24-hour format: "1630", "16:30", "16 30", "16h30", "16h"
  const time24Match = timeStr.match(/^(\d{1,2})[\s:hH]?(\d{2})?$/);
  if (time24Match) {
    let hours: number;
    let minutes: number;

    if (
      timeStr.includes(':') ||
      timeStr.includes(' ') ||
      timeStr.toLowerCase().includes('h')
    ) {
      hours = parseInt(time24Match[1]!);
      minutes = parseInt(time24Match[2] || '0');
    } else {
      // "1630" format
      const num = timeStr.padStart(4, '0');
      hours = parseInt(num.slice(0, 2));
      minutes = parseInt(num.slice(2, 4));
    }

    if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
      const now = nowWithOffsetDate();
      now.setHours(hours, minutes, 0, 0);
      return now.toISOString();
    }
  }

  return null;
}

export function formatTime(timestamp: number): string;
export function formatTime(isoString: string): string;
export function formatTime(isoString: Date): string;
export function formatTime(time: number | string | Date): string {
  if (time === '') {
    return '';
  }
  let date: Date;
  if (typeof time === 'string' || typeof time === 'number') {
    date = new Date(time);
  } else {
    date = time;
  }
  // we don't want localized time, just HH:MM in 24-hour format
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function formatDate(date: number | string | Date): string {
  const d = new Date(date);
  const month = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  const year = d.getUTCFullYear();
  return (
    year +
    '-' +
    month.toString().padStart(2, '0') +
    '-' +
    day.toString().padStart(2, '0')
  );
}
